import { useState, useRef, useCallback, useEffect } from "react";

export function useAutosave({ api, sections, editedContent, isSaved, generationContext, exportMeta }) {
  const [draftKey] = useState(() => {
    const ct = generationContext?.content_type || exportMeta?.content_type || "content";
    return `${ct.toLowerCase().replace(/\s+/g, "_")}_${Date.now()}`;
  });
  const [draftId, setDraftId] = useState(null);
  const [autosaveStatus, setAutosaveStatus] = useState(null);
  const [lastSavedTime, setLastSavedTime] = useState(null);
  const autosaveTimer = useRef(null);

  const hasUnsavedEdits = Object.keys(editedContent).length > 0;

  const performAutosave = useCallback(async () => {
    if (isSaved) return;
    const mergedSections = { ...sections };
    Object.keys(editedContent).forEach((k) => { mergedSections[k] = editedContent[k]; });

    setAutosaveStatus("saving");
    try {
      const resp = await api.post("/history/draft", {
        draft_key: draftKey,
        content_type: generationContext?.content_type || exportMeta?.content_type || "",
        sections: mergedSections,
        generation_context: generationContext || {},
      });
      setDraftId(resp.data.draft_id);
      setAutosaveStatus("saved");
      setLastSavedTime(new Date());
    } catch {
      setAutosaveStatus("error");
    }
  }, [api, draftKey, editedContent, sections, isSaved, generationContext, exportMeta]);

  useEffect(() => {
    if (!hasUnsavedEdits || isSaved) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => { performAutosave(); }, 3000);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
  }, [editedContent, hasUnsavedEdits, isSaved, performAutosave]);

  useEffect(() => {
    if (!hasUnsavedEdits) return;
    const handleBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedEdits]);

  const deleteDraft = useCallback(async () => {
    if (!draftId) return;
    try { await api.delete(`/history/draft/${draftId}`); } catch { /* silent */ }
    setDraftId(null);
    setAutosaveStatus(null);
  }, [api, draftId]);

  return { draftId, autosaveStatus, lastSavedTime, hasUnsavedEdits, deleteDraft };
}
